import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { countWords } from '@helios/utils'
import { ErrorMessage } from '@northfork/ng-basics'

const NOTES_LENGTH = 3

export const NOTE_MAX_LENGTH = 1024

// Positive allow-list mirroring the backend (BreakUpdateRequest.kt): Unicode letters,
// digits, punctuation, math/currency symbols, and whitespace separators (incl. tab/
// newline/CR). `<` and `>` are excluded so raw HTML/script tags can't be submitted and
// resurface in a downstream renderer.
const ALLOWED_NOTE_PATTERN =
  /^[\p{L}\p{N}\p{P}\p{Sm}\p{Sc}\p{So}\p{Sk}\p{Z}\t\n\r]*$/u

/**
 * Function to check if note is valid.
 * Note is valid when it has a minimum length of 3 words.
 */
export function checkIsNoteValid(str: string | null | undefined): boolean {
  return countWords(str) >= NOTES_LENGTH
}

/**
 * Validator for note entry forms in Helios, which typically requires
 * each note to have a minimum length of three words.
 *
 * @param allowEmpty allow empty values, false by default.
 */
export function NotesValidator(allowEmpty: boolean = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const val: string = control?.value
    const isString = typeof val === 'string'
    const isEmpty =
      val === undefined || val === null || (isString && val.length === 0)
    const isNoteValid = checkIsNoteValid(val)
    if (!isString || (allowEmpty && isEmpty) || isNoteValid) {
      return null
    }

    return {
      NotesValidator: {},
    }
  }
}

/**
 * Validator that constrains note content to mirror the backend's input validation.
 * Rejects notes longer than {@link NOTE_MAX_LENGTH} or containing characters outside
 * the allow-list (notably `<` and `>`, to prevent stored XSS payloads).
 */
export function NoteContentValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const val: unknown = control?.value
    if (typeof val !== 'string' || val.length === 0) return null
    if (val.length > NOTE_MAX_LENGTH) {
      return { NoteTooLong: { maxLength: NOTE_MAX_LENGTH } }
    }
    // The explicit `<`/`>` deny is load-bearing: both characters fall under
    // \p{Sm} (math symbols), so the allow-list alone would let them through.
    if (
      val.includes('<') ||
      val.includes('>') ||
      !ALLOWED_NOTE_PATTERN.test(val)
    ) {
      return { NoteInvalidCharacters: {} }
    }
    return null
  }
}

export const notesValidationMessages: ErrorMessage[] = [
  {
    error: 'NotesValidator',
    format: (label, error): string => {
      return `Insufficient justification, note field must have ${NOTES_LENGTH} words minimum.`
    },
  },
  {
    error: 'NoteTooLong',
    format: (label, error): string => {
      return `Note must be at most ${NOTE_MAX_LENGTH} characters.`
    },
  },
  {
    error: 'NoteInvalidCharacters',
    format: (label, error): string => {
      return `Note contains invalid characters (e.g. '<' or '>').`
    },
  },
]
